import React from 'react';
import Panel from 'react-bootstrap/lib/Panel';
import {
  faUserCircle,
  faCalendarAlt,
} from '@fortawesome/free-regular-svg-icons';

import {
  faPencilAlt,
  faDollarSign,
  faCog,
} from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Link from '../../../components/Link';
import Question from '../../../components/Help/Questions/Question';

class HelpForExpertsSection extends React.Component {
  render() {
    return (
      <React.Fragment>
        <Panel>
          <Panel.Heading>Getting Started as an Expert</Panel.Heading>
          <Panel.Body>
            <Question
              answer={
                <div>
                  Yes, every user has the option to create a shadowing profile
                  and let other people shadow them.
                </div>
              }
              question="Can I let people shadow me?"
            />

            <Question
              answer={
                <div>
                  We only require that you are knowledgeable in your area of
                  expertise and have a desire to share that knowledge with
                  others.
                </div>
              }
              question="What are the qualifications to be an expert?"
            />

            <Question
              answer={
                <div>
                  You can create a shadowing profile for any skill, piece of
                  technology or trade about which you're passionate and
                  knowledgeable. If your area of expertise is not listed as one
                  of the subcategories, contact us if you would like to suggest
                  another category or subcategory as we plan to add more in the
                  near future.
                </div>
              }
              question="What should I teach?"
            />

            <Question
              answer={
                <div>
                  The <Link to="/handbook">Shadowing Handbook</Link> provides
                  concrete steps to create a successful shadowing session.
                </div>
              }
              question="What resources are provided for me?"
            />

            <Question
              answer={
                <div>
                  Self-employed experts can simply link their personal or
                  professional bank account to Shadower and begin receiving
                  payments from shadowers. The same goes for experts who work
                  for a company, but schedule shadowing sessions around company
                  time and property.
                </div>
              }
              question="How does it work if I’m self-employed?"
            />

            <Question
              answer={
                <div>
                  Experts who are not self-employed should consult with their
                  employer before creating a shadowing profile if they plan to
                  allow shadowers during company hours, on company property or
                  using company intellectual property or technology. We
                  recommend the following options if you are not self-employed:
                  <ul>
                    <li>
                      If just you would like to participate:
                      <p>
                        After receiving permission, simply sign up and create a
                        shadowing profile like normal, linking your own personal
                        bank account.
                      </p>
                    </li>
                    <li>
                      If part or all of your company would like to participate:
                      <p>
                        You or your employer could create a shadowing profile
                        for each major section of the business. Note that since
                        each shadowing profile allows an unlimited number of
                        unique shadowing sessions, each with their own title,
                        description, price and restrictions, a single shadowing
                        profile might be fully sufficient for a small business.
                        We suggest that the employer then links the various
                        shadowing profiles (if more than one have been created
                        for the company) to the same business bank account or
                        PayPal account and appropriately distributes the funds
                        as he or she sees fit at the end of the week or month.
                      </p>
                    </li>

                    <li>
                      If you would like to participate but your company
                      prohibits you:
                      <p>
                        You will have to let people shadow you during your
                        non-work hours and with your own equipment.
                      </p>
                    </li>
                  </ul>
                </div>
              }
              question="How does it work if I’m NOT self-employed?"
            />

            <Question
              answer={
                <div>
                  Yes, all expert service fees are waived for non-profit
                  organizations registered as a 501(c)(3), meaning 100 percent
                  of the money paid by shadowers goes toward the organization.
                  If you work for a non-profit and you would like your service
                  fees waived, please contact us.
                </div>
              }
              question="Do you waive fees for non-profits?"
            />
          </Panel.Body>
        </Panel>
        <Panel>
          <Panel.Heading>About the Sessions</Panel.Heading>
          <Panel.Body>
            <Question
              answer={
                <div>
                  You will fill out a calendar that lists all available
                  shadowing opportunities. You can schedule the sessions during
                  normal work hours or around them, depending on what you would
                  like to accomplish with your shadower.
                  <br />
                  <br />
                  If you have a lot of availability and don't want to fill out
                  the calendar, you can leave it empty, but you must tell
                  potential shadowers to contact you to schedule a time. If
                  someone requests to shadow you, for safety and verification
                  purposes, we require that you then go and create a session for
                  that person to book. Having a record of the session will help
                  protect you in the case of a dispute with your shadower. It
                  also helps to establish the validity of your profile by
                  increasing the number of booked sessions, reviews and scores
                  listed. Failing to follow these guidelines could result in a
                  suspension from the platform.
                </div>
              }
              question="How does scheduling work?"
            />

            <Question
              answer={
                <div>
                  We strongly recommend that you provide supplemental education
                  material before, during or after your session, such as a
                  written description of what you did, tips and tricks, a
                  screencast, etc. For ideas about what to provide, refer to the{' '}
                  <Link to="/handbook/planning-a-session/providing-resources">
                    Providing Resources
                  </Link>{' '}
                  section of the <Link to="/handbook">Shadowing Handbook</Link>.
                </div>
              }
              question="What should I provide shadowers?"
            />

            <Question
              answer={
                <div>
                  Noncompete agreements formally restrict an employee from
                  engaging in competitive activities. In Colorado, noncompete
                  agreements are not allowed unless the agreement deals with:
                  <ul>
                    <li>The sale of a business</li>
                    <li>Trade secrets</li>
                    <li>Management or management personnel</li>
                  </ul>
                  For the purposes of shadowing, experts who are not
                  self-employed should understand what constitutes a trade
                  secret. Trade secrets are information that:
                  <ul>
                    <li>The company has taken measures to obtain or develop</li>
                    <li>
                      The company has an interest in keeping from a competitor
                    </li>
                    <li>Relates to the business</li>
                    <li>Has value to the business</li>
                    <li>The owner has taken measures to keep secret</li>
                  </ul>
                  Every shadower must agree to a{' '}
                  <Link to="/policies/shadowing-waiver">Shadowing Waiver</Link>{' '}
                  before they can shadow you. In that waiver, they are expressly
                  forbidden from conveying any confidential information, trade
                  secrets, business plans or proprietary information related to
                  your company. If a shadower violates the confidentiality
                  clause, you are entitled to immediate injunctive or other
                  equitable relief under law. Of course, we believe it's simply
                  best to avoid the issue altogether and recommend experts do
                  not divulge any sort of trade secrets or propriety information
                  while shadowing. <br />
                  <br />
                  Learn more about{' '}
                  <a
                    href="https://www.robinsonandhenry.com/colorado/business/noncompete-agreements/"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    noncompete agreements
                  </a>{' '}
                  or consult with your employer if you have concerns.
                </div>
              }
              question="Do I have to worry about noncompete agreements?"
            />

            <Question
              answer={
                <div>
                  After creating a new session, click the checkbox under the
                  pair of glasses labeled <strong>Observation Only</strong> to
                  signify that the session will have no interactive components.
                  Checking only <strong>Interactive </strong> signifies the
                  shadower will be engaged in a project or activity for most of
                  the session. Checking both boxes signifies that the shadower
                  will participate in some hands-on training and observing other
                  parts.
                </div>
              }
              question="How do I set a session's level of interactivity?"
            />

            <Question
              answer={
                <div>
                  Experts are allowed to decline a shadowing request based on
                  factors not prohibited by law, such as issues of safety or
                  legal age restrictions as explained in our non-discrimination
                  policy below under{' '}
                  <Link to="/help/trust-and-safety">Community Guidelines</Link>.{' '}
                  <br />
                  <br /> Experts should strive to make shadowers feel welcome
                  and included regardless of background as inclusion only
                  strengthens the shadowing community.
                </div>
              }
              question="Can I decline a shadower’s booking?"
            />
          </Panel.Body>
        </Panel>
        <Panel>
          <Panel.Heading>Using the Platform</Panel.Heading>
          <Panel.Body>
            <Question
              answer={
                <div>
                  If the session has not been booked yet, you can edit all
                  aspects of the session, including the title, description,
                  interactivity and so on by navigating to{' '}
                  <strong>
                    <FontAwesomeIcon icon={faUserCircle} />
                    &nbsp; Profiles
                  </strong>{' '}
                  , selecting your shadowing profile, clicking the date on the
                  calendar that contains the session you would like to change
                  and then clicking on{' '}
                  <strong>
                    {' '}
                    <FontAwesomeIcon icon={faPencilAlt} />
                    &nbsp; Edit
                  </strong>{' '}
                  to go into edit mode and make any necessary changes.
                </div>
              }
              question="How do I update a previously created session?"
            />

            <Question
              answer={
                <div>
                  Click on your <strong>Calendar</strong> and navigate to{' '}
                  <strong>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    &nbsp; Shadowing Me
                  </strong>{' '}
                  to view all of the upcoming sessions that users have booked
                  with you.{' '}
                  {/* At the bottom, you can click on
                <strong>Previous Sessions</strong> to view all of
                the sessions that you have led as an expert. */}
                </div>
              }
              question="How do I view my sessions booked with me?"
            />

            {/*   <Question
              answer={
                <div>
                  You can message your shadower by navigating to their personal
                  profile and clicking on{' '}
                  <strong>
                    <FontAwesomeIcon icon={faEnvelope} />
                    &nbsp; Contact
                  </strong>{' '}
                  under their profile photo. All messages will be available in
                  your{' '}
                  <strong>
                    <FontAwesomeIcon icon={faInbox} />
                    &nbsp; Inbox
                  </strong>
                  .
                </div>
              }
              question="How do I message my shadower?"
            /> */}

            <Question
              answer={
                <div>
                  You should fully inform you shadowers about any risks
                  associated with shadowing you, any requirements for
                  participation, such as minimum age, skill level, fitness or
                  other necessary conditions and anything else they may need to
                  know to safely shadow them, such as dress code, equipment
                  needs, special certifications or licenses, etc. This
                  information and more is laid out in our{' '}
                  <Link to="/policies/terms-of-service">Terms of Service</Link>.
                </div>
              }
              question="What should I let shadowers know ahead of time?"
            />

            <Question
              answer={
                <div>
                  Yes, you can. We recommend experts create a different profile
                  for each area of expertise (e.g. one for music production and
                  another for graphic design).
                </div>
              }
              question="Can I create multiple shadowing profiles?"
            />
          </Panel.Body>
        </Panel>
        <Panel>
          <Panel.Heading>Earning Revenue</Panel.Heading>
          <Panel.Body>
            <Question
              answer={
                <div>
                  You keep 90 percent of whatever you charge per session.
                </div>
              }
              question="How much can I make letting people shadow me?"
            />

            <Question
              answer={
                <div>
                  To receive payment, you will need to link your PayPal or bank
                  account to Shadower. All money is transferred securely through
                  Stripe. Navigate to your{' '}
                  <strong>
                    <FontAwesomeIcon icon={faCog} />
                    &nbsp; Settings
                  </strong>{' '}
                  and go to{' '}
                  <strong>
                    <FontAwesomeIcon icon={faDollarSign} />
                    &nbsp; Payout Methods
                  </strong>{' '}
                  to set up your Stripe account.
                </div>
              }
              question="How do I receive payment?"
            />

            {/*  <Question
              answer={
                <div>
                  In the{' '}
                  <strong>
                    <FontAwesomeIcon icon={faDollarSign} />
                    &nbsp; Payout Methods
                  </strong>{' '}
                  tab of your{' '}
                  <strong>
                    <FontAwesomeIcon icon={faCog} />
                    &nbsp; Settings
                  </strong>{' '}
                  page, you can view all payouts through Stripe.
                </div>
              }
              question="How do I view my payout history?"
            /> */}
          </Panel.Body>
        </Panel>
      </React.Fragment>
    );
  }
}

export default HelpForExpertsSection;
